"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause } from "lucide-react";

const initialMarkers = [
  { id: 1, position: 20, time: "00:20" },
  { id: 2, position: 10, time: "00:10" },
  { id: 3, position: 50, time: "00:50" },
  { id: 4, position: 70, time: "00:55" },
  { id: 5, position: 80, time: "01:50" },
  { id: 6, position: 90, time: "01:10" },
  { id: 7, position: 40, time: "01:60" },
];

const TimelineMarker = ({ position }) => (
  <div
    className="absolute bottom-[-5px] transform -translate-x-1/2"
    style={{ left: `${position}%` }}
  >
    <img
      src="../assets/images/red-flag.png"
      alt="red-flag"
      className="w-[32px] h-[50px] aspect-video object-cover"
    />
  </div>
);

export default function Process() {
  const [progress, setProgress] = useState(0); // Initialize progress state

  const [video, setVideo] = useState(null);
  const [sensitiveContent, setSensitiveContent] = useState([]);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [numberOfScenes, setNumberOfScenes] = useState(null);
  const [mainGerne, setMainGenre] = useState("");
  const [primaryGenre, setPrimaryGenre] = useState("");
  const [secondaryGenre, setSecondaryGenre] = useState("");
  const [justification, setJustification] = useState("");
  const [recommendedRating, setRecommendedRating] = useState("");
  const [flagPositions, setFlagPositions] = useState([]);
  const [hoveredTimestamp, setHoveredTimestamp] = useState(null); // Track hovered timestamp
  const [videoDuration, setVideoDuration] = useState("00:00"); // Track video duration
  const [currentTime, setCurrentTime] = useState(0);
  // Utility to format timestamp to remove milliseconds
  const formatTimestamp = (timestamp) => {
    const [minutes, seconds] = timestamp.split(":");
    const formattedSeconds = seconds.split(".")[0]; // Remove milliseconds
    return `${minutes}:${formattedSeconds}`;
  };

  // Calculate marker position based on timestamp

  useEffect(() => {
    const savedVideo = localStorage.getItem("processingVideo");
    const savedResponse = localStorage.getItem("processingVideoResponse");

    if (savedVideo) {
      setVideo(JSON.parse(savedVideo)); // Load video details
    }

    if (savedResponse) {
      const parsedData = JSON.parse(savedResponse);

      // Extract and split the `text` content for processing
      const contentText = parsedData?.output?.message?.content?.[0]?.text || "";

      // Split the text into lines
      const lines = contentText.split("\n").map((line) => line.trim());

      // Extract Number of Scenes Detected
      const numberOfScenesMatch = contentText.match(
        /\*\*Number of Scenes Detected:\*\*\s*(\d+)/
      );
      const numberOfScenes = numberOfScenesMatch
        ? parseInt(numberOfScenesMatch[1], 10)
        : null;

      // Extract Primary and Secondary Genres
      const primaryGenreMatch = lines.find((line) =>
        line.startsWith("- **Primary Genre:**")
      );
      const secondaryGenreMatch = lines.find((line) =>
        line.startsWith("- **Secondary Genre:**")
      );
      const primaryGenre = primaryGenreMatch
        ? primaryGenreMatch.split(":**")[1]?.trim()
        : null;
      const secondaryGenre = secondaryGenreMatch
        ? secondaryGenreMatch.split(":**")[1]?.trim()
        : null;

      // Extract Recommended Rating
      const recommendedRatingMatch = contentText.match(
        /\*\*Recommended Rating:\*\*\s*(\w[-\w+]*)/
      );
      const recommendedRating = recommendedRatingMatch
        ? recommendedRatingMatch[1]?.trim()
        : null;

      // Extract Justification
      const justificationMatch = contentText.match(
        /\*\*Justification:\*\*\s*(.*)/
      );
      const justification = justificationMatch
        ? justificationMatch[1]?.trim()
        : null;

      // Parse "Content Review" into timestamps and descriptions
      const contentReviewIndex = lines.findIndex((line) =>
        line.startsWith("2. **Content Review:**")
      );
      const sceneData = [];
      if (contentReviewIndex !== -1) {
        for (let i = contentReviewIndex + 1; i < lines.length; i++) {
          const match = lines[i].match(
            /-\s*(\d{2}:\d{2}\.\d{3}\s*-\s*\d{2}:\d{2}\.\d{3}):\s*(.*)/
          );
          if (match) {
            sceneData.push({
              timestamp: match[1], // Extract timestamp
              description: match[2], // Extract description
            });
          }
        }
      }

      // Update state with the parsed data
      setSensitiveContent(sceneData);
      setNumberOfScenes(numberOfScenes);
      setPrimaryGenre(primaryGenre);
      setSecondaryGenre(secondaryGenre);
      setRecommendedRating(recommendedRating);
      setJustification(justification);
    }
  }, []);

  const calculateFlagPositions = () => {
    if (!videoRef.current || !videoRef.current.duration) {
      console.warn("Video duration not loaded yet");
      return;
    }

    const positions = sensitiveContent.map((item) => {
      // Extract start time
      const timestamp = item.timestamp.split("-")[0]?.trim(); // Get the first part (start time)
      const [minutes, seconds] = timestamp.split(":").map(parseFloat); // Convert to numbers
      const totalSeconds = minutes * 60 + seconds; // Convert to total seconds

      const position = (totalSeconds / videoRef.current.duration) * 100; // Calculate percentage position
      return {
        position,
        timestamp: formatTimestamp(timestamp), // Format to 'mm:ss'
      };
    });

    setFlagPositions(positions);
  };

  useEffect(() => {
    const videoElement = videoRef.current;

    if (videoElement) {
      // Wait for video metadata to load before calculating positions
      videoElement.addEventListener("loadedmetadata", () => {
        setVideoDuration(formatDuration(videoElement.duration)); // Set video duration
        calculateFlagPositions(); // Calculate flag positions
      });

      return () => {
        videoElement.removeEventListener(
          "loadedmetadata",
          calculateFlagPositions
        );
      };
    }
  }, [sensitiveContent]);

  // Format duration in hh:mm:ss
  const formatDuration = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  // Update progress on video `timeupdate` event
  useEffect(() => {
    const videoElement = videoRef.current;
  
    if (videoElement) {
      // Update progress and currentTime dynamically during playback
      const handleTimeUpdate = () => {
        const currentProgress =
          (videoElement.currentTime / videoElement.duration) * 100;
        setProgress(currentProgress);
        setCurrentTime(videoElement.currentTime); // Update currentTime
      };
  
      // Set video duration when metadata loads
      const handleLoadedMetadata = () => {
        setVideoDuration(formatDuration(videoElement.duration));
      };
  
      videoElement.addEventListener("timeupdate", handleTimeUpdate);
      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
  
      return () => {
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);
        videoElement.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
      };
    }
  }, [videoRef]);

  const VideoProgress = ({ videoRef, progress, setProgress }) => {
    const progressRef = useRef(null);
    const frameId = useRef(null);

    const updateProgress = (videoRef, setProgress) => {
      if (!videoRef.current) return;
      const newProgress =
        (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(newProgress);
    };
    
    const handleClick = (e, progressRef, videoRef, setProgress) => {
      if (!videoRef.current || !videoRef.current.duration) return;
    
      const progressBarRect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - progressBarRect.left;
      const clickPercentage = Math.min(Math.max(clickX / progressBarRect.width, 0), 1);
    
      const newTime = videoRef.current.duration * clickPercentage;
      videoRef.current.currentTime = newTime;
      setProgress(clickPercentage * 100);
    };
    
    const handleMouseMove = (e, progressRef, videoRef, tooltipId) => {
      if (!videoRef.current || !videoRef.current.duration) return;
    
      const progressBarRect = progressRef.current.getBoundingClientRect();
      const hoverX = e.clientX - progressBarRect.left;
      const hoverPercentage = Math.min(Math.max(hoverX / progressBarRect.width, 0), 1);
    
      const hoverTime = videoRef.current.duration * hoverPercentage;
      const tooltip = document.getElementById(tooltipId);
      if (tooltip) {
        tooltip.style.left = `${hoverX}px`;
        tooltip.textContent = formatDuration(hoverTime);
        tooltip.style.display = "block";
      }
    };
    
    const handleMouseLeave = (tooltipId) => {
      const tooltip = document.getElementById(tooltipId);
      if (tooltip) {
        tooltip.style.display = "none";
      }
    };
    
    useEffect(() => {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      const animateProgress = () => {
        updateProgress();
        frameId.current = requestAnimationFrame(animateProgress);
      };

      frameId.current = requestAnimationFrame(animateProgress);

      return () => {
        if (frameId.current) {
          cancelAnimationFrame(frameId.current);
        }
      };
    }, [updateProgress]);

    return (
      <div ref={progressRef} className="relative h-2 bg-white rounded-full">
        <div
          className="absolute top-0 left-0 h-full bg-purple-600 rounded-full"
          style={{ width: `${progress}%` }}
        />
      </div>
    );
  };
  const togglePlayPause = async () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    try {
      if (isPlaying) {
        videoElement.pause();
        setIsPlaying(false);
      } else {
        await videoElement.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("Error toggling play/pause:", error);
      setIsPlaying(false);
    }
  };

  if (!video) return null;
  const handleLogout = () => {
    // Clear the entire localStorage
    localStorage.clear();

    // Navigate to the login page
    window.location.href = "/ai/home";
  };

  return (
    <main className="min-h-screen w-full relative">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10"
        style={{
          backgroundImage: "url(../assets/images/home-bg.png)",
        }}
      />

      {/* Container */}
      <div className="container mx-auto px-4 py-8 relative">
        {/* Logout Button */}
        <div className="absolute top-6 right-4">
          <button
            onClick={handleLogout} // Replace with your logout function
            className="px-6 py-2 px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow"
          >
            Home
          </button>
        </div>

        {/* Header */}
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-8 heading-text">
          Media Sentinel
        </h1>

        {/* Grid Layout */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Left Side - Video Player */}
          <div
            className="relative rounded-lg overflow-hidden bg-black/80"
            style={{ height: "600px", width: "100%", maxWidth: "1200px" }}
          >
            {/* Video Player */}
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              playsInline
            >
              <source src={video?.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>

            {/* Play/Pause Overlay */}
            <button
              onClick={togglePlayPause}
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
                isPlaying
                  ? "opacity-0 hover:opacity-100 bg-black/10 hover:bg-black/30"
                  : "bg-black/30"
              } group`}
            >
              <div className="rounded-full p-4 bg-purple-600/80 group-hover:bg-purple-600 transition-colors">
                {isPlaying ? (
                  <Pause className="w-12 h-12 text-white" />
                ) : (
                  <Play className="w-12 h-12 text-white" />
                )}
              </div>
            </button>
          </div>

          {/* Right Side - Buttons */}
          <div className="flex flex-col justify-center items-center gap-4">
            <button className="w-full px-4 py-3 px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow">
              {numberOfScenes
                ? `Number of sensitive scenes detected: ${numberOfScenes}`
                : "Button 1"}
            </button>
            <button className="w-full px-4 py-3 px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow">
              <div>
                <p>{mainGerne}</p>
                <p className="mt-2">{primaryGenre}</p>
                <p className="mt-2">{secondaryGenre}</p>
              </div>
            </button>
            <button className="w-full px-4 py-3 px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow">
              {recommendedRating
                ? `Recommended Rating: ${recommendedRating}`
                : "Button 1"}
            </button>
          </div>
        </div>

        {/* Timeline */}
        <button className="w-full px-4 py-3 px-10 py-3 my-5 text-white rounded-lg font-medium relative group overflow-hidden button-shadow">
          {justification ? justification : "Button 5"}
        </button>
        <div className="relative w-full h-16 flex flex-col items-center mx-4 my-4">
  {/* Progress Bar Container */}
  <div
    className="relative w-full h-2 bg-white rounded-full cursor-pointer"
    onClick={(e) => {
      if (!videoRef.current || !videoRef.current.duration) return;

      // Get bounding rectangle of the progress bar
      const progressBarRect = e.currentTarget.getBoundingClientRect();

      // Calculate the click position relative to the progress bar
      const clickX = e.clientX - progressBarRect.left;
      const progressBarWidth = progressBarRect.width;

      // Ensure the percentage is clamped between 0 and 1
      const clickPercentage = Math.min(
        Math.max(clickX / progressBarWidth, 0),
        1
      );

      // Calculate the corresponding time in the video
      const newTime = videoRef.current.duration * clickPercentage;

      // Update the video's current time
      videoRef.current.currentTime = newTime;

      // Update progress and currentTime state
      setProgress(clickPercentage * 100);
      setCurrentTime(newTime);
    }}
  >
    {/* Progress Indicator */}
    <div
      className="absolute top-0 left-0 h-full bg-purple-600 rounded-full"
      style={{ width: `${progress}%` }}
    />

    {/* Flags */}
    {flagPositions.map((flag, index) => (
      <div
        key={index}
        className="absolute top-[-40px] transform -translate-x-1/2 cursor-pointer"
        style={{ left: `${flag.position}%` }}
        onClick={(e) => {
          e.stopPropagation(); // Prevent bubbling to the progress bar

          if (videoRef.current) {
            // Parse the timestamp (e.g., "01:30")
            const [minutes, seconds] = flag.timestamp
              .split(":")
              .map(parseFloat);
            const timeInSeconds = minutes * 60 + seconds;

            // Set the video's current time to the flag's position
            videoRef.current.currentTime = timeInSeconds;

            // Update progress and currentTime state
            setProgress(
              (timeInSeconds / videoRef.current.duration) * 100
            );
            setCurrentTime(timeInSeconds);
          }
        }}
        onMouseEnter={() => setHoveredTimestamp(flag.timestamp)}
        onMouseLeave={() => setHoveredTimestamp(null)}
      >
        <img
          src="../assets/images/red-flag.png"
          alt="red-flag"
          className="w-[32px] h-[50px] object-cover"
        />
        {hoveredTimestamp === flag.timestamp && (
          <div className="absolute top-[-25px] left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded">
            {hoveredTimestamp}
          </div>
        )}
      </div>
    ))}
  </div>

  {/* Timestamps */}
  <div className="flex justify-between w-full mt-2">
    {/* Current Timestamp */}
    <span className="text-white text-sm">
      {formatDuration(currentTime)}
    </span>

    {/* Total Duration */}
    <span className="text-white text-sm">{videoDuration}</span>
  </div>
</div>


        {/* Sensitive Content Section */}
        <div className="h-[320px] overflow-y-auto scrollbar-hide space-y-4 mt-6">
          {sensitiveContent.map((item, index) => (
            <div key={index} className="flex gap-4 items-center">
              <div className="bg-white flex items-center text-lg rounded px-4 py-3 text-black min-w-[150px] text-center">
                {item.timestamp}
              </div>
              <div className="flex-1 bg-white rounded px-4 py-3">
                <p className="text-lg text-black">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
